import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useDoctor, useDeleteDoctor, useSendResetLink, useGenerateResetLink } from '../../services/httpServices/queries';
import { AppointmentPrintModal } from '../../components/ui/AppointmentPrintModal';
import { ChangePasswordModal } from '../../components/ui/ChangePasswordModal';
import { ChangeUsernameModal } from '../../components/ui/ChangeUsernameModal';
import { CopyResetLinkModal } from '../../components/ui/CopyResetLinkModal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import {
  AlertCircle,
  ArrowLeft,
  Award,
  Banknote,
  Briefcase,
  Building2,
  CalendarClock,
  CalendarDays,
  Clock,
  Edit3,
  Globe,
  Mail,
  MapPin,
  Percent,
  Phone,
  Printer,
  Shield,
  Stethoscope,
  Trash2,
  UserRound,
} from 'lucide-react';
import {
  Chip,
  DetailRow,
  EmptyState,
  InfoTile,
  ProfilePhoto,
  ProfileSkeleton,
  SectionCard,
  StatTile,
  StatusPill,
  TabBar,
  UserAccountCard,
  type ProfileTab,
} from '../../components/ui/profile-ui';

type DoctorTab = 'overview' | 'schedule' | 'account';

const money = (value?: number | string | null) => `৳ ${Number(value || 0).toLocaleString()}`;

const feeText = (value?: number | string | null) =>
  value === undefined || value === null ? '—' : money(value);

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

export function DoctorView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useDoctor(id || '');
  const deleteDoctor = useDeleteDoctor();
  const sendResetLink = useSendResetLink();
  const generateResetLink = useGenerateResetLink();
  const [tab, setTab] = useState<DoctorTab>('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showResetLinkModal, setShowResetLinkModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);

  const doctor = data?.data;

  const doctorDepartments = doctor?.departments?.length
    ? doctor.departments
    : doctor?.department
      ? [doctor.department]
      : [];

  // Guard against legacy/malformed schedule data (e.g. nested empty arrays)
  // so invalid entries never render as blank rows.
  const consultationAvailability = (doctor?.consultationAvailability ?? []).filter(
    (entry) =>
      entry &&
      typeof entry.day === 'string' &&
      entry.day.trim() !== '' &&
      Array.isArray(entry.timeRanges) &&
      entry.timeRanges.length > 0,
  );
  const sessionCount = consultationAvailability.reduce(
    (total, entry) => total + entry.timeRanges.length,
    0,
  );

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteDoctor.mutateAsync(id);
      navigate('/doctors');
    } catch {
      // error handled by mutation
    }
  };

  const handleCopyResetLink = async (): Promise<string | null> => {
    if (!doctor?.user?.id) return null;
    try {
      const res = await generateResetLink.mutateAsync(doctor.user.id);
      const url = res?.data?.url;
      if (!url) throw new Error('No reset link returned');
      return url;
    } catch {
      toast.error('Failed to generate reset link');
      return null;
    }
  };

  const handleResendResetLink = async () => {
    if (!doctor?.user?.id) return;
    try {
      await sendResetLink.mutateAsync(doctor.user.id);
      toast.success(`Password reset link sent to ${doctor.user.email || doctor.user.username}`);
      setShowResendModal(false);
    } catch {
      toast.error('Failed to send reset link');
    }
  };

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (error || !doctor) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-2xl border border-danger/20 bg-danger/5 p-8 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-danger/10 text-danger">
          <AlertCircle size={20} />
        </span>
        <p className="text-sm font-semibold text-danger">Failed to load doctor details</p>
        <p className="max-w-sm text-xs text-text-muted">
          The doctor may have been removed, or the server is unavailable. Please try again.
        </p>
        <button
          type="button"
          onClick={() => navigate('/doctors')}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text transition-colors hover:border-primary/40"
        >
          <ArrowLeft size={14} />
          Back to Doctors
        </button>
      </div>
    );
  }

  const roomFloor =
    doctor.consultationRoom?.floor?.name ||
    (doctor.consultationRoom?.floor?.floorNumber !== undefined
      ? `Floor ${doctor.consultationRoom.floor.floorNumber}`
      : '');

  const tabs: ProfileTab<DoctorTab>[] = [
    { id: 'overview', label: 'Overview', icon: UserRound },
    { id: 'schedule', label: 'Schedule', icon: CalendarClock, count: consultationAvailability.length },
    { id: 'account', label: 'Account', icon: Shield },
  ];

  return (
    <div className="w-full space-y-4">
      {/* Compact toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => navigate('/doctors')}
            aria-label="Back to doctors"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/70 bg-surface text-text-muted shadow-sm transition-colors hover:border-primary/40 hover:text-text"
          >
            <ArrowLeft size={17} />
          </button>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              HR Mgmt. / Doctors
            </p>
            <p className="text-sm font-semibold text-text">Doctor profile</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPrintModal(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border/70 bg-surface px-3 text-[13px] font-medium text-text shadow-sm transition-colors hover:border-primary/40"
          >
            <Printer size={15} />
            <span className="hidden sm:inline">Print appointments</span>
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            aria-label="Delete doctor"
            title="Delete doctor"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-danger/20 bg-danger/5 text-danger transition-colors hover:bg-danger/10"
          >
            <Trash2 size={16} />
          </button>
          <button
            type="button"
            onClick={() => navigate(`/doctors/${id}/edit`)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-primary-dark"
          >
            <Edit3 size={15} />
            Edit
          </button>
        </div>
      </div>

      {/* Profile hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border/70 bg-surface shadow-sm">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 left-1/3 h-56 w-56 rounded-full bg-secondary/10 blur-3xl" />

        <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center">
          <ProfilePhoto src={doctor.photoUrl} name={doctor.name} active={doctor.isActive} />

          <div className="min-w-0 flex-1 space-y-3.5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold tracking-tight text-text sm:text-2xl">{doctor.name}</h1>
                  <StatusPill active={doctor.isActive} />
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${
                      doctor.showOnWebsite
                        ? 'bg-sky-500/10 text-sky-600 ring-sky-500/25 dark:text-sky-400'
                        : 'bg-border/50 text-text-muted ring-border'
                    }`}
                  >
                    <Globe size={11} />
                    {doctor.showOnWebsite ? 'On website' : 'Hidden'}
                  </span>
                </div>
                <p className="mt-0.5 text-[13px] text-text-muted">
                  {doctor.designation?.name || 'No designation'}
                  {doctor.registrationNumber && (
                    <span className="text-text-muted"> · Reg. {doctor.registrationNumber}</span>
                  )}
                </p>
                {doctor.specializations && doctor.specializations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {doctor.specializations.map((specialization) => (
                      <Chip key={specialization.id}>{specialization.label}</Chip>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              <InfoTile icon={Stethoscope} label="Doctor code" value={doctor.employeeCode} mono />
              <InfoTile
                icon={Building2}
                label="Departments"
                value={doctorDepartments.length > 0 ? doctorDepartments.map((d) => d.name).join(', ') : '—'}
              />
              <InfoTile
                icon={Award}
                label="Specializations"
                value={
                  doctor.specializations && doctor.specializations.length > 0
                    ? doctor.specializations.map((s) => s.label).join(', ')
                    : '—'
                }
              />
              <InfoTile icon={MapPin} label="Consultation room" value={doctor.consultationRoom?.name || '—'} />
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-text-muted">
              {doctor.phone && (
                <a
                  href={`tel:${doctor.phone}`}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-text"
                >
                  <Phone size={13} />
                  {doctor.phone}
                </a>
              )}
              {doctor.email && (
                <a
                  href={`mailto:${doctor.email}`}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-text"
                >
                  <Mail size={13} />
                  {doctor.email}
                </a>
              )}
              {roomFloor && (
                <span className="inline-flex items-center gap-1.5">
                  <Building2 size={13} />
                  {roomFloor}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Fee snapshot */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile icon={Banknote} label="New patient" value={feeText(doctor.consultationFeeNew)} hint="Consultation fee" />
        <StatTile
          icon={CalendarClock}
          label="Follow-up"
          value={feeText(doctor.consultationFeeFollowup)}
          hint="Follow-up visit"
        />
        <StatTile
          icon={Briefcase}
          label="Report check"
          value={feeText(doctor.consultationFeeReport)}
          hint="Report review"
        />
        <StatTile
          icon={Percent}
          label="Revenue share"
          value={`${Number(doctor.revenueSharePercent) || 0}%`}
          hint="Referral share"
          tone="accent"
        />
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabBar tabs={tabs} active={tab} onChange={setTab} />
        <p className="hidden text-[11px] text-text-muted sm:block">
          {consultationAvailability.length} day{consultationAvailability.length === 1 ? '' : 's'} ·{' '}
          {sessionCount} session{sessionCount === 1 ? '' : 's'} / week
        </p>
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard
            icon={Briefcase}
            title="Professional details"
            subtitle="Registration, role and expertise"
          >
            <div className="space-y-0">
              <DetailRow label="Registration number" value={doctor.registrationNumber || '—'} mono />
              <DetailRow label="Designation" value={doctor.designation?.name || '—'} />
              <DetailRow
                label="Departments"
                value={doctorDepartments.length > 0 ? doctorDepartments.map((d) => d.name).join(', ') : '—'}
              />
              <DetailRow
                label="Specializations"
                value={
                  doctor.specializations && doctor.specializations.length > 0
                    ? doctor.specializations.map((s) => s.label).join(', ')
                    : '—'
                }
              />
              <DetailRow
                label="Referral revenue share"
                value={`${Number(doctor.revenueSharePercent) || 0}%`}
              />
            </div>

            <div className="mt-4">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                Qualifications
              </p>
              {doctor.qualifications && doctor.qualifications.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {doctor.qualifications.map((qualification, index) => (
                    <Chip key={`${qualification}-${index}`} tone="muted">
                      {qualification}
                    </Chip>
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-border bg-background/50 px-3 py-3 text-center text-[12px] text-text-muted">
                  No qualifications recorded.
                </p>
              )}
            </div>
          </SectionCard>

          <SectionCard icon={Stethoscope} title="Practice" subtitle="Consultation setup and visibility">
            <div className="space-y-0">
              <DetailRow label="Consultation room" value={doctor.consultationRoom?.name || '—'} />
              <DetailRow label="Floor" value={roomFloor || '—'} />
              <DetailRow
                label="Website listing"
                value={
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      doctor.showOnWebsite
                        ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
                        : 'bg-border/50 text-text-muted'
                    }`}
                  >
                    <Globe size={11} />
                    {doctor.showOnWebsite ? 'Visible' : 'Hidden'}
                  </span>
                }
              />
              <DetailRow
                label="Scheduled days"
                value={`${consultationAvailability.length} day${consultationAvailability.length === 1 ? '' : 's'} / week`}
              />
              <DetailRow
                label="Weekly sessions"
                value={`${sessionCount} session${sessionCount === 1 ? '' : 's'}`}
              />
              <DetailRow label="Profile updated" value={formatDate(doctor.updatedAt || doctor.createdAt)} />
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setTab('schedule')}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border/70 bg-background/60 px-3 py-2 text-[13px] font-medium text-text transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
              >
                <CalendarClock size={14} />
                View schedule
              </button>
              <button
                type="button"
                onClick={() => navigate(`/doctors/${id}/edit`)}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border/70 bg-background/60 px-3 py-2 text-[13px] font-medium text-text transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
              >
                <Edit3 size={14} />
                Edit profile
              </button>
            </div>
          </SectionCard>
        </div>
      )}

      {tab === 'schedule' && (
        <SectionCard
          icon={CalendarClock}
          title="Consultation availability"
          subtitle="Weekly consultation hours"
          action={
            <span className="rounded-full bg-background px-2.5 py-1 text-[11px] font-medium text-text-muted">
              {sessionCount} session{sessionCount === 1 ? '' : 's'} / week
            </span>
          }
        >
          {consultationAvailability.length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title="No consultation schedule set"
              hint="Edit this doctor to add the days and time ranges they are available."
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {consultationAvailability.map((availability, idx) => (
                <div
                  key={`${availability.day}-${idx}`}
                  className="rounded-xl border border-border/60 bg-background/50 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-[11px] font-bold text-primary">
                        {availability.day.slice(0, 3)}
                      </span>
                      <p className="text-[13px] font-semibold text-text">{availability.day}</p>
                    </div>
                    <span className="text-[11px] text-text-muted">
                      {availability.timeRanges.length} session
                      {availability.timeRanges.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  <div className="mt-2.5 space-y-1.5">
                    {(availability.timeRanges ?? []).map((range, ridx) => (
                      <div
                        key={`${range.from}-${range.to}-${ridx}`}
                        className="flex items-center gap-2 rounded-lg border border-border/50 bg-surface px-2.5 py-1.5"
                      >
                        <Clock size={13} className="shrink-0 text-primary" />
                        <span className="min-w-0 truncate text-[12px] font-medium text-text">
                          {range.title || 'Untitled'}
                        </span>
                        <span className="ml-auto shrink-0 text-[11px] tabular-nums text-text-muted">
                          {range.from} – {range.to}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {tab === 'account' && (
        <>
          {doctor.user ? (
            <UserAccountCard
              user={doctor.user}
              resending={sendResetLink.isPending}
              generating={generateResetLink.isPending}
              onResendLink={() => setShowResendModal(true)}
              onCopyResetLink={() => setShowResetLinkModal(true)}
              onChangeUsername={() => setShowUsernameModal(true)}
              onChangePassword={() => setShowPasswordModal(true)}
              onManageRoles={() => navigate(`/users/${doctor.user!.id}/roles`)}
            />
          ) : (
            <SectionCard icon={Shield} title="System user account" subtitle="Sign-in identity and access">
              <EmptyState
                icon={UserRound}
                title="No user account linked"
                hint="Edit this doctor to create a sign-in account and assign roles."
              />
            </SectionCard>
          )}
        </>
      )}

      {/* Modals */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete doctor?"
        description={
          <>
            Are you sure you want to delete <strong className="text-text">{doctor.name}</strong>? This
            action cannot be undone.
          </>
        }
        icon={<Trash2 size={18} />}
        confirmLabel="Delete doctor"
        tone="danger"
        loading={deleteDoctor.isPending}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />

      {showUsernameModal && doctor.user && (
        <ChangeUsernameModal
          user={doctor.user}
          onClose={() => setShowUsernameModal(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['doctors', id] })}
        />
      )}

      {showPasswordModal && doctor.user && (
        <ChangePasswordModal user={doctor.user} onClose={() => setShowPasswordModal(false)} />
      )}

      {showPrintModal && (
        <AppointmentPrintModal initialDoctorId={doctor.id} onClose={() => setShowPrintModal(false)} />
      )}

      <CopyResetLinkModal
        open={showResetLinkModal}
        userName={doctor.name}
        userEmail={doctor.user?.email}
        onConfirm={handleCopyResetLink}
        onClose={() => setShowResetLinkModal(false)}
      />

      <ConfirmDialog
        open={showResendModal}
        title="Resend password reset link?"
        description={
          <>
            A password reset link will be emailed to{' '}
            <span className="font-medium text-text">{doctor.user?.email}</span>.
          </>
        }
        icon={<Mail size={18} />}
        confirmLabel="Send link"
        loading={sendResetLink.isPending}
        onConfirm={handleResendResetLink}
        onClose={() => setShowResendModal(false)}
      />
    </div>
  );
}
